import { useEffect, useState } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { DateField } from "@/shared/ui/date-field";
import { FormField } from "@/shared/ui/form-field";
import { Label } from "@/shared/ui/label";
import { useLanguage } from "@/shared/hooks/use-language";
import { useToast } from "@/shared/hooks/use-toast";
import { parseBackendErrors } from "@/shared/lib/backend-errors";
import { formatAmountScale, parseDecimalToAmountScale } from "@/shared/lib/amount";
import { composeReferenceTime, formatDate } from "@/shared/lib/date-utils";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import type { ScopeContext } from "@/features/auth";
import { createPositionSnapshots } from "@/features/portfolio/api/position-snapshot";
import { accountScopeOf } from "@/features/portfolio/api/portfolio";
import type { Position } from "@/features/portfolio/api/position";

interface DeclareSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioId: string;
  position: Position | null;
  onDeclared: (snapshotId: string) => void;
}

const newUniqueId = () => crypto.randomUUID();

const SNAPSHOT_FIELD_MAP: Record<string, string> = {
  symbol_amount: "quantity",
  symbol_scale: "quantity",
  price_amount: "price",
  price_scale: "price",
  reference_time: "referenceTime",
};

export const DeclareSnapshotDialog = ({
  open,
  onOpenChange,
  portfolioId,
  position,
  onDeclared,
}: DeclareSnapshotDialogProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext: ScopeContext | null = sessionId
    ? ScopeContextStorage.getScopeContext(sessionId)
    : null;
  const accountScope = scopeContext ? accountScopeOf(scopeContext) : null;

  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [referenceTime, setReferenceTime] = useState("");
  const [referenceTimeOfDay, setReferenceTimeOfDay] = useState("");
  const [uniqueId, setUniqueId] = useState(newUniqueId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));

  const resetDeclaration = () => {
    setQuantity("");
    setPrice("");
    setReferenceTime(formatDate(new Date(), "YYYY-MM-DD"));
    setReferenceTimeOfDay("");
    setFieldErrors({});
    setUniqueId(newUniqueId());
  };

  useEffect(() => {
    if (open) resetDeclaration();
  }, [open]);

  const handleSubmit = async () => {
    const nextFieldErrors: Record<string, string> = {};
    const parsedQuantity = parseDecimalToAmountScale(quantity);
    const parsedPrice = price.trim() === "" ? undefined : parseDecimalToAmountScale(price);
    if (!parsedQuantity) {
      nextFieldErrors.quantity = t("portfolio.quantityInvalid");
    }
    if (parsedPrice === null || (parsedPrice && parsedPrice.amount < 0)) {
      nextFieldErrors.price = t("portfolio.priceInvalid");
    }
    if (!referenceTime) nextFieldErrors.referenceTime = t("portfolio.snapshotReferenceTimeRequired");
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    if (!position || !parsedQuantity) return;
    if (!sessionId || !accountScope) {
      toast({ title: t("portfolio.noAccountScope"), variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await createPositionSnapshots(sessionId, accountScope, [{
        portfolio_id: portfolioId,
        instrument_id: position.instrument_id,
        symbol_amount: parsedQuantity.amount,
        symbol_scale: parsedQuantity.scale,
        ...(parsedPrice
          ? { price_amount: parsedPrice.amount, price_scale: parsedPrice.scale }
          : {}),
        reference_time: composeReferenceTime(referenceTime, referenceTimeOfDay),
        unique_id: uniqueId,
      }]);
      toast({ title: t("portfolio.snapshotDeclared") });
      const declared = response.snapshots[0];
      resetDeclaration();
      onDeclared(declared.id);
    } catch (err) {
      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
        err,
        SNAPSHOT_FIELD_MAP,
      );
      setFieldErrors(backendFieldErrors);
      const message = isUnreadableResponseError(err)
        ? t("errors.unreadableResponse")
        : generalError;
      if (message) {
        toast({ title: message, variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("portfolio.snapshotTitle")}</DialogTitle>
          <DialogDescription>{t("portfolio.snapshotDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label id="snapshot-instrument-label">{t("portfolio.instrument")}</Label>
              <p
                aria-labelledby="snapshot-instrument-label"
                data-testid="snapshot-instrument-display"
                className="flex h-10 items-center rounded-md border border-border bg-muted px-3 text-sm"
              >
                {position ? position.symbol || position.instrument_id : ""}
              </p>
            </div>
            <div className="space-y-2">
              <Label id="snapshot-current-quantity-label">
                {t("portfolio.snapshotCurrentQuantity")}
              </Label>
              <p
                aria-labelledby="snapshot-current-quantity-label"
                data-testid="snapshot-current-quantity"
                className="flex h-10 items-center rounded-md border border-border bg-muted px-3 text-sm tabular-nums"
              >
                {position
                  ? formatAmountScale(position.symbol_amount, position.symbol_scale, { trimZeros: true })
                  : ""}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              id="snapshot-quantity"
              label={t("portfolio.snapshotQuantity")}
              inputMode="decimal"
              value={quantity}
              error={fieldErrors.quantity}
              onChange={(value: string) => {
                setQuantity(value);
                clearFieldError("quantity");
              }}
            />
            <FormField
              id="snapshot-price"
              label={t("portfolio.snapshotPrice")}
              inputMode="decimal"
              value={price}
              error={fieldErrors.price}
              onChange={(value: string) => {
                setPrice(value);
                clearFieldError("price");
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="snapshot-price-hint">
            {t("portfolio.snapshotPriceHint")}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DateField
              id="snapshot-reference-time"
              label={t("portfolio.snapshotReferenceTime")}
              value={referenceTime}
              error={fieldErrors.referenceTime}
              onChange={(value: string) => {
                setReferenceTime(value);
                clearFieldError("referenceTime");
              }}
            />
            <FormField
              id="snapshot-reference-time-of-day"
              label={t("portfolio.referenceTimeOfDay")}
              type="time"
              value={referenceTimeOfDay}
              onChange={setReferenceTimeOfDay}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("portfolio.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} data-testid="snapshot-submit">
            {isSubmitting ? t("portfolio.snapshotDeclaring") : t("portfolio.snapshotDeclare")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
